import React, { useState, useEffect } from 'react';
import Banner from '../components/common/Banner';
import { apiService } from '../services/api';
import { SecureContent } from '../utils/sanitize';
import { getImageUrl } from '../utils/imageUtils';

const AcademicsPage = () => {
  const [activeSection, setActiveSection] = useState('courses');
  const [academicsData, setAcademicsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Grievance form state
  const [grievanceForm, setGrievanceForm] = useState({
    fullName: '',
    studentId: '',
    email: '',
    phoneNumber: '',
    department: '',
    grievanceDescription: '',
    category: '',
    isAnonymous: false
  });
  const [grievanceErrors, setGrievanceErrors] = useState({});
  const [grievanceSubmitting, setGrievanceSubmitting] = useState(false);
  const [grievanceMessage, setGrievanceMessage] = useState({ type: '', content: '' });

  // Default data (will be replaced by API data)
  const defaultCourses = {
    btech: [
      { code: 'CE', name: 'Civil Engineering', description: 'Infrastructure development and construction technology' },
      { code: 'CSE', name: 'Computer Science & Engineering', description: 'Software development and computational technologies' },
      { code: 'ECE', name: 'Electronics & Communication Engineering', description: 'Electronics and communication systems' },
      { code: 'ME', name: 'Mechanical Engineering', description: 'Mechanical systems and manufacturing technology' }
    ],
    polytechnic: [
      { code: 'ECE', name: 'Electronics & Communication Engineering', description: 'Diploma in Electronics and Communication' },
      { code: 'ME', name: 'Mechanical Engineering', description: 'Diploma in Mechanical Engineering' }
    ]
  };
  
  const courses = academicsData ? {
    btech: academicsData.btechCourses || defaultCourses.btech,
    polytechnic: academicsData.polytechnicCourses || defaultCourses.polytechnic
  } : defaultCourses;

  const defaultDepartments = [
    {
      id: 'ce',
      name: 'Department of Civil Engineering',
      description: 'The Civil Engineering department focuses on infrastructure development, construction technology, and sustainable engineering practices.',
      labs: ['Concrete Technology Lab', 'Surveying Lab', 'Geotechnical Engineering Lab', 'Environmental Engineering Lab', 'CAD Lab']
    },
    {
      id: 'cse',
      name: 'Department of Computer Science & Engineering',
      description: 'The CSE department emphasizes software development, artificial intelligence, and cutting-edge computational technologies.',
      labs: ['Programming Lab', 'Data Structures Lab', 'Database Management Lab', 'Network Security Lab', 'AI/ML Lab', 'Software Engineering Lab']
    },
    {
      id: 'ece',
      name: 'Department of Electronics & Communication Engineering',
      description: 'The ECE department covers electronics systems, communication technologies, and embedded systems design.',
      labs: ['Digital Electronics Lab', 'Communication Systems Lab', 'Microprocessor Lab', 'VLSI Design Lab', 'Signal Processing Lab']
    },
    {
      id: 'me',
      name: 'Department of Mechanical Engineering',
      description: 'The Mechanical Engineering department focuses on manufacturing, thermal systems, and mechanical design.',
      labs: ['Manufacturing Technology Lab', 'Thermal Engineering Lab', 'Fluid Mechanics Lab', 'Machine Design Lab', 'CAD/CAM Lab']
    }
  ];
  
  const departments = academicsData?.departments || defaultDepartments;

  const defaultAntiRaggingActs = [
    'Any conduct by students that causes or is likely to cause physical or psychological harm',
    'Verbal abuse, threats, or intimidation of any form',
    'Forcing students to do any act which causes embarrassment, humiliation or psychological harm',
    'Any act of financial extortion or forceful expenditure',
    'Forcing students to perform tasks that are not related to academics',
    'Physical assault, sexual abuse or any form of misconduct',
    'Deliberate intent to cause mental harassment or create fear among junior students',
    'Any activity that disrupts the normal academic environment and student welfare'
  ];
  
  const antiRaggingActs = academicsData?.antiRaggingActs || defaultAntiRaggingActs;

  useEffect(() => {
    fetchAcademicsData();
  }, []);
  
  const fetchAcademicsData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAcademics();
      console.log('Academics data received:', response.data);
      console.log('Banner image value:', response.data?.bannerImage);
      setAcademicsData(response.data);
    } catch (error) {
      console.error('Error fetching academics data:', error);
      setError('Failed to load academics content');
    } finally {
      setLoading(false);
    }
  };

  // Handle grievance form changes
  const handleGrievanceFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setGrievanceForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (grievanceErrors[name]) {
      setGrievanceErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate grievance form
  const validateGrievanceForm = () => {
    const errors = {};
    
    if (!grievanceForm.fullName.trim()) {
      errors.fullName = 'Full name is required';
    }
    
    if (!grievanceForm.studentId.trim()) {
      errors.studentId = 'Student ID is required';
    }
    
    if (!grievanceForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(grievanceForm.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!grievanceForm.grievanceDescription.trim()) {
      errors.grievanceDescription = 'Grievance description is required';
    } else if (grievanceForm.grievanceDescription.length < 10) {
      errors.grievanceDescription = 'Please provide more details (at least 10 characters)';
    }
    
    return errors;
  };

  // Handle grievance form submission
  const handleGrievanceSubmit = async (e) => {
    e.preventDefault();
    setGrievanceMessage({ type: '', content: '' });
    
    // Validate form
    const errors = validateGrievanceForm();
    if (Object.keys(errors).length > 0) {
      setGrievanceErrors(errors);
      return;
    }
    
    setGrievanceSubmitting(true);
    setGrievanceErrors({});
    
    try {
      const response = await apiService.submitGrievance(grievanceForm);
      
      if (response.success) {
        setGrievanceMessage({
          type: 'success',
          content: 'Your grievance has been submitted successfully! You will receive a confirmation email shortly. Reference ID: ' + response.data.id
        });
        
        // Reset form
        setGrievanceForm({
          fullName: '',
          studentId: '',
          email: '',
          phoneNumber: '',
          department: '',
          grievanceDescription: '',
          category: '',
          isAnonymous: false
        });
      } else {
        setGrievanceMessage({
          type: 'error',
          content: response.message || 'Failed to submit grievance. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error submitting grievance:', error);
      setGrievanceMessage({
        type: 'error',
        content: 'An error occurred while submitting your grievance. Please try again later.'
      });
    } finally {
      setGrievanceSubmitting(false);
    }
  };

  // Reset grievance form
  const resetGrievanceForm = () => {
    setGrievanceForm({
      fullName: '',
      studentId: '',
      email: '',
      phoneNumber: '',
      department: '',
      grievanceDescription: '',
      category: '',
      isAnonymous: false
    });
    setGrievanceErrors({});
    setGrievanceMessage({ type: '', content: '' });
  };

  const renderCoursesSection = () => (
    <div className="space-y-8">
      <div className="mb-6 sm:mb-8">
        <h2 className="text-2xl sm:text-3xl font-sans font-bold text-slate mb-3 sm:mb-4">Courses Offered</h2>
        <p className="text-sm sm:text-base text-gray-500">Comprehensive engineering education programs designed for industry readiness.</p>
      </div>

      <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
        {/* B.Tech Courses */}
        <div className="bg-white p-4 sm:p-6 rounded-xl border border-border_gray">
          <h3 className="text-lg sm:text-xl font-bold text-crimson mb-3 sm:mb-4 flex items-center">
            <i className="fas fa-graduation-cap mr-2"></i>
            Bachelor of Technology (B.Tech)
          </h3>
          <div className="space-y-4">
            {courses.btech.map((course, index) => (
              <div key={index} className="p-4 bg-surface rounded-lg border border-gray-200">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold text-slate">{course.name}</h4>
                    <p className="text-sm text-gray-500 mt-1">{course.description}</p>
                  </div>
                  <span className="bg-crimson text-white px-2 py-1 text-xs font-bold rounded">{course.code}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Polytechnic Courses */}
        <div className="bg-white p-4 sm:p-6 rounded-xl border border-border_gray">
          <h3 className="text-lg sm:text-xl font-bold text-crimson mb-3 sm:mb-4 flex items-center">
            <i className="fas fa-tools mr-2"></i>
            Polytechnic (Diploma)
          </h3>
          <div className="space-y-4">
            {courses.polytechnic.map((course, index) => (
              <div key={index} className="p-4 bg-surface rounded-lg border border-gray-200">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold text-slate">{course.name}</h4>
                    <p className="text-sm text-gray-500 mt-1">{course.description}</p>
                  </div>
                  <span className="bg-gold text-white px-2 py-1 text-xs font-bold rounded">{course.code}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderDepartmentsSection = () => (
    <div className="space-y-8">
      <div className="mb-6 sm:mb-8">
        <h2 className="text-2xl sm:text-3xl font-sans font-bold text-slate mb-3 sm:mb-4">Departments</h2>
        <p className="text-sm sm:text-base text-gray-500">Specialized departments with state-of-the-art facilities and expert faculty.</p>
      </div>

      <div className="space-y-6">
        {departments.map((dept) => (
          <div key={dept.id} className="bg-white p-4 sm:p-6 md:p-8 rounded-xl border border-border_gray">
            <h3 className="text-lg sm:text-xl font-bold text-crimson mb-3 sm:mb-4">{dept.name}</h3>
            <div className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
              <SecureContent content={dept.description} className="rich-text-content" />
            </div>
            
            <div>
              <h4 className="font-semibold text-slate mb-3">Laboratory Facilities:</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                {dept.labs.map((lab, index) => (
                  <div key={index} className="bg-surface px-3 py-2 rounded border border-gray-200 text-sm text-gray-700">
                    <i className="fas fa-flask text-crimson mr-2"></i>
                    {lab}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAntiRaggingSection = () => (
    <div className="space-y-8">
      <div className="mb-8">
        <h2 className="text-3xl font-sans font-bold text-slate mb-4">{academicsData?.antiRaggingTitle || 'Anti-Ragging Policy'}</h2>
        <p className="text-gray-500">{academicsData?.antiRaggingDescription || 'Creating a safe and conducive learning environment for all students.'}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="bg-white p-6 rounded-xl border border-border_gray">
          <h3 className="text-xl font-bold text-crimson mb-4">Acts that Constitute Ragging</h3>
          <ul className="space-y-3">
            {antiRaggingActs.map((act, index) => (
              <li key={index} className="flex items-start">
                <span className="bg-red-100 text-red-600 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-0.5">
                  {index + 1}
                </span>
                <span className="text-gray-600 text-sm">{act}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white p-6 rounded-xl border border-border_gray">
          <h3 className="text-xl font-bold text-crimson mb-4">Anti-Ragging Squad</h3>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center text-yellow-800">
              <i className="fas fa-shield-alt text-yellow-600 mr-2"></i>
              <span className="font-semibold">{academicsData?.antiRaggingHelpline || '24/7 Anti-Ragging Helpline'}</span>
            </div>
            <p className="text-yellow-700 text-sm mt-2">
              Students can report any incidents confidentially through our dedicated helpline.
            </p>
          </div>
          
          <div className="space-y-3">
            <p className="text-sm text-gray-600 font-semibold">Committee Members:</p>
            <div className="text-sm text-gray-500">
              {(academicsData?.antiRaggingCommittee || ['Faculty Coordinator', 'Student Representatives', 'Administrative Staff', 'External Expert Members']).map((member, index) => (
                <p key={index}>• {member}</p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderGrievanceSection = () => (
    <div className="space-y-8">
      <div className="mb-8">
        <h2 className="text-3xl font-sans font-bold text-slate mb-4">{academicsData?.grievanceTitle || 'Student Grievance'}</h2>
        <p className="text-gray-500">{academicsData?.grievanceDescription || 'Submit your concerns and grievances through our online portal.'}</p>
      </div>

      {academicsData?.grievanceFormEnabled !== false && (
        <div className="max-w-2xl mx-auto bg-white p-8 rounded-xl border border-border_gray">
          <h3 className="text-xl font-bold text-crimson mb-6">Grievance Registration Form</h3>
          
          {/* Success/Error Message */}
          {grievanceMessage.content && (
            <div className={`mb-6 p-4 rounded-lg ${
              grievanceMessage.type === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-800' 
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              <div className="flex items-center">
                <i className={`fas ${grievanceMessage.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'} mr-2`}></i>
                {grievanceMessage.content}
              </div>
            </div>
          )}
          
          <form className="space-y-6" onSubmit={handleGrievanceSubmit}>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                <input 
                  type="text" 
                  name="fullName"
                  value={grievanceForm.fullName}
                  onChange={handleGrievanceFormChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:border-crimson outline-none ${
                    grievanceErrors.fullName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your full name"
                  disabled={grievanceSubmitting}
                />
                {grievanceErrors.fullName && (
                  <p className="text-red-600 text-sm mt-1">{grievanceErrors.fullName}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Student ID *</label>
                <input 
                  type="text" 
                  name="studentId"
                  value={grievanceForm.studentId}
                  onChange={handleGrievanceFormChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:border-crimson outline-none ${
                    grievanceErrors.studentId ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter student ID"
                  disabled={grievanceSubmitting}
                />
                {grievanceErrors.studentId && (
                  <p className="text-red-600 text-sm mt-1">{grievanceErrors.studentId}</p>
                )}
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                <input 
                  type="email" 
                  name="email"
                  value={grievanceForm.email}
                  onChange={handleGrievanceFormChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:border-crimson outline-none ${
                    grievanceErrors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter email address"
                  disabled={grievanceSubmitting}
                />
                {grievanceErrors.email && (
                  <p className="text-red-600 text-sm mt-1">{grievanceErrors.email}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                <input 
                  type="tel" 
                  name="phoneNumber"
                  value={grievanceForm.phoneNumber}
                  onChange={handleGrievanceFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-crimson outline-none"
                  placeholder="Enter phone number"
                  disabled={grievanceSubmitting}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Department</label>
                <select 
                  name="department"
                  value={grievanceForm.department}
                  onChange={handleGrievanceFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-crimson outline-none"
                  disabled={grievanceSubmitting}
                >
                  <option value="">Select Department</option>
                  <option value="Civil Engineering">Civil Engineering</option>
                  <option value="Computer Science & Engineering">Computer Science & Engineering</option>
                  <option value="Electronics & Communication Engineering">Electronics & Communication Engineering</option>
                  <option value="Mechanical Engineering">Mechanical Engineering</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                <select 
                  name="category"
                  value={grievanceForm.category}
                  onChange={handleGrievanceFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-crimson outline-none"
                  disabled={grievanceSubmitting}
                >
                  <option value="">Select Category</option>
                  <option value="Academic">Academic</option>
                  <option value="Administrative">Administrative</option>
                  <option value="Infrastructure">Infrastructure</option>
                  <option value="Faculty">Faculty</option>
                  <option value="Hostel">Hostel</option>
                  <option value="Harassment">Harassment</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Grievance Description * (Max 3000 characters)</label>
              <textarea 
                rows="6"
                name="grievanceDescription"
                value={grievanceForm.grievanceDescription}
                onChange={handleGrievanceFormChange}
                className={`w-full px-4 py-3 border rounded-lg focus:border-crimson outline-none ${
                  grievanceErrors.grievanceDescription ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Please describe your grievance in detail..."
                maxLength="3000"
                disabled={grievanceSubmitting}
              ></textarea>
              <div className="flex justify-between items-center mt-1">
                {grievanceErrors.grievanceDescription && (
                  <p className="text-red-600 text-sm">{grievanceErrors.grievanceDescription}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {grievanceForm.grievanceDescription.length}/3000 characters
                </p>
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="isAnonymous"
                checked={grievanceForm.isAnonymous}
                onChange={handleGrievanceFormChange}
                className="mr-2"
                disabled={grievanceSubmitting}
              />
              <label className="text-sm text-gray-700">
                Submit anonymously (your name will not be disclosed)
              </label>
            </div>

            <div className="flex gap-4">
              <button 
                type="submit"
                className="bg-crimson hover:bg-red-800 text-white font-bold py-3 px-8 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={grievanceSubmitting}
              >
                {grievanceSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Submitting...
                  </>
                ) : (
                  'Submit Grievance'
                )}
              </button>
              <button 
                type="button"
                onClick={resetGrievanceForm}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-3 px-8 rounded-lg transition disabled:opacity-50"
                disabled={grievanceSubmitting}
              >
                Reset Form
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );

  const renderFacultyStaffSection = () => (
    <div className="space-y-8">
      <div className="mb-8">
        <h2 className="text-3xl font-sans font-bold text-slate mb-4">{academicsData?.facultyTitle || 'Faculty & Staff'}</h2>
        <p className="text-gray-500">{academicsData?.facultyDescription || 'Our commitment to excellence in engineering education.'}</p>
      </div>

      <div className="bg-white p-8 rounded-xl border border-border_gray">
        <div className="rich-text-content text-gray-600">
          <h3 className="text-xl font-bold text-crimson mb-6">Excellence in Education</h3>
          
          <div className="mb-6">
            <SecureContent 
              content={academicsData?.facultyContent?.introduction || 'At Forte Institute of Technology, we pride ourselves on having a distinguished faculty and dedicated staff who are committed to nurturing the next generation of engineering professionals. Our academic community comprises experienced educators, industry experts, and passionate researchers who bring a wealth of knowledge and real-world experience to the classroom.'} 
              className="rich-text-content"
            />
          </div>

          {(academicsData?.facultyContent?.sections || [
            {
              title: 'Faculty Excellence',
              content: 'Our faculty members hold advanced degrees from prestigious institutions across India and abroad. With an average teaching experience of over 15 years, they combine academic expertise with industry insights to deliver comprehensive education. The faculty-to-student ratio is maintained at an optimal level to ensure personalized attention and mentorship for every student.'
            },
            {
              title: 'Academic Leadership', 
              content: 'The institute is led by visionary academic leaders who have decades of experience in engineering education and research. Our Principal, Vice Principal, and Deans work collaboratively to maintain high academic standards, implement innovative teaching methodologies, and foster a culture of continuous improvement.'
            },
            {
              title: 'Support Staff',
              content: 'Beyond our teaching faculty, we have a dedicated team of administrative and technical staff who ensure smooth functioning of all academic and co-curricular activities. From laboratory assistants to library staff, each member plays a crucial role in creating a conducive learning environment.'
            },
            {
              title: 'Professional Development',
              content: 'We believe in continuous learning and regularly organize faculty development programs, workshops, and conferences. Our faculty members are encouraged to pursue research, publish papers, and participate in national and international academic forums. This ensures they stay updated with the latest technological advancements and pedagogical innovations.'
            },
            {
              title: 'Student-Centric Approach',
              content: 'Every member of our faculty and staff is committed to student success. We maintain an open-door policy where students can approach faculty members for academic guidance, career counseling, or personal mentorship. Regular feedback mechanisms ensure that teaching methods are continuously refined to meet student needs.'
            }
          ]).map((section, index) => (
            <div key={index}>
              <h4 className="text-lg font-semibold text-slate mb-4">{section.title}</h4>
              <SecureContent content={section.content} className="mb-6 rich-text-content" />
            </div>
          ))}

          {/* Faculty Members Table */}
          {academicsData?.facultyMembers && academicsData.facultyMembers.length > 0 && (
            <div className="mt-8">
              <h4 className="text-lg font-bold text-slate mb-4 flex items-center">
                <i className="fas fa-chalkboard-teacher text-crimson mr-2"></i>
                Our Faculty Members
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-slate text-white">
                      <th className="px-4 py-3 text-left text-sm font-semibold rounded-tl-lg">S.No.</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Faculty Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Department</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold rounded-tr-lg">Designation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {academicsData.facultyMembers.map((member, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="px-4 py-3 text-sm text-gray-600 border-b border-gray-200">{index + 1}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-800 border-b border-gray-200">{member.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 border-b border-gray-200">{member.department}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 border-b border-gray-200">{member.designation}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
            <h4 className="text-lg font-bold text-blue-800 mb-4 flex items-center">
              <i className="fas fa-envelope text-blue-600 mr-2"></i>
              Contact Information
            </h4>
            <div className="grid md:grid-cols-2 gap-6 text-sm">
              <div>
                <p className="text-blue-700 font-semibold mb-2">{academicsData?.facultyContactInfo?.academic?.title || 'Academic Inquiries'}</p>
                <p className="text-blue-600">📧 {academicsData?.facultyContactInfo?.academic?.email || 'academics@fit.ac.in'}</p>
                <p className="text-blue-600">📞 {academicsData?.facultyContactInfo?.academic?.phone || '+91 98765-43200'}</p>
                <p className="text-blue-600 mt-2">Office Hours: {academicsData?.facultyContactInfo?.academic?.hours || 'Mon-Fri, 9:00 AM - 5:00 PM'}</p>
              </div>
              <div>
                <p className="text-blue-700 font-semibold mb-2">{academicsData?.facultyContactInfo?.administrative?.title || 'Administrative Office'}</p>
                <p className="text-blue-600">📧 {academicsData?.facultyContactInfo?.administrative?.email || 'info@fit.ac.in'}</p>
                <p className="text-blue-600">📞 {academicsData?.facultyContactInfo?.administrative?.phone || '+91 98765-43201'}</p>
                <p className="text-blue-600 mt-2">Office Hours: {academicsData?.facultyContactInfo?.administrative?.hours || 'Mon-Sat, 8:30 AM - 4:30 PM'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const sections = [
    { id: 'courses', label: 'Courses Offered', component: renderCoursesSection },
    { id: 'departments', label: 'Departments', component: renderDepartmentsSection },
    { id: 'faculty-staff', label: 'Faculty & Staff', component: renderFacultyStaffSection },
    { id: 'anti-ragging', label: 'Anti-Ragging', component: renderAntiRaggingSection },
    { id: 'grievance', label: 'Grievance', component: renderGrievanceSection }
  ];

  if (loading) {
    return (
      <div className="h-full w-full fade-in flex items-center justify-center min-h-screen">
        <div className="text-lg">
          <i className="fas fa-spinner fa-spin mr-2"></i>
          Loading academics content...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full w-full fade-in flex items-center justify-center min-h-screen">
        <div className="text-red-600">
          <i className="fas fa-exclamation-triangle mr-2"></i>
          {error}
        </div>
      </div>
    );
  }

  // Handle both filename and full URL cases
  let bannerImageUrl = "https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80";
  
  if (academicsData?.bannerImage) {
    // If it's already a full URL (starts with http), use as-is
    if (academicsData.bannerImage.startsWith('http')) {
      bannerImageUrl = academicsData.bannerImage;
    } else {
      // Otherwise, it's a filename that needs to be converted to full URL
      bannerImageUrl = getImageUrl(academicsData.bannerImage);
    }
  }
  
  console.log('Banner image URL being used:', bannerImageUrl);
  console.log('Raw banner image value:', academicsData?.bannerImage);
  
  return (
    <div className="h-full w-full  fade-in">
      <Banner 
        title={academicsData?.bannerTitle || "Academics"}
        subtitle={academicsData?.bannerSubtitle || "Comprehensive Engineering Programs | Building Future Innovators"}
        imageSrc={bannerImageUrl}
      />
      <div className="max-w-6xl mx-auto p-4 sm:p-6 md:p-10">
        
        {/* Section Navigation */}
        <div className="mb-6 sm:mb-8 border-b border-gray-200">
          <div className="flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`py-3 sm:py-4 px-1 sm:px-2 border-b-2 text-sm sm:text-base font-semibold whitespace-nowrap transition-all ${
                  activeSection === section.id
                    ? 'border-crimson text-crimson'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {section.label}
              </button>
            ))}
          </div>
        </div>

        {/* Active Section Content */}
        {sections.find(section => section.id === activeSection)?.component()}

      </div>
    </div>
  );
};

export default AcademicsPage;